/**
 * ✅ 할 일 — /tasks (v2.319.0 · 2026-11-01 · Salesforce Tasks · 기능·메뉴 검토 F4)
 *
 *   내 것(담당이 나 · 내가 만든 것) / 전체 · 열림 / 완료 / 전체 · 기한(전체 · 지남 · 오늘 · 이번 주 · 없음) · 글자 검색
 *   표에서 바로: ✓ 완료 · 제목(누르면 고침) · 기한 · 우선순위 · 상태 · 줄 끝 ▼(지우기) · 연결 대상(영업건·거래처·딜 바로 열림)
 *   한 줄 추가(제목 · 기한 · 우선순위 — 담당은 나). 영업건에 딸린 할 일은 영업건 상세 「활동」에서도 넣는다.
 *   주소 ?status=open&due=overdue|today 는 헤더 🔔·홈 「오늘」이 보낸다. viewer 는 이 화면이 없다(App.tsx ViewerBlocked · 메뉴 hideForViewer).
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import {
  createTask, deleteTask, dueLabel, listTasks, patchTask, taskRelatedPath,
  TASK_PRIORITIES, TASK_STATUSES, type Task, type TaskPriority, type TaskStatus,
} from '../lib/tasks-api';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { RowMenu } from '../components/ui/RowMenu';
import { LoadingLines } from '../components/ui/Skeleton';
import { LastUpdateBadge } from '../components/LastUpdateBadge';

type Scope = 'mine' | 'all';
type StatusFilter = 'open' | 'done' | 'all';
type DueFilter = 'all' | 'overdue' | 'today' | 'week' | 'none';

const RELATED_LABEL: Record<string, string> = { sales: '영업건', partner: '거래처', maintenance: '유지보수', deal: '딜등록' };

function todayIso(): string {
  const d = new Date(Date.now() + 9 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}
function plusDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10);
}

export function TasksPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const scope = (sp.get('scope') as Scope) || 'mine';
  const status = (sp.get('status') as StatusFilter) || 'open';
  const due = (sp.get('due') as DueFilter) || 'all';
  const q = sp.get('q') ?? '';
  const setParam = (k: string, v: string): void => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };

  const listQ = useQuery({ queryKey: ['tasks', scope, status, q], queryFn: () => listTasks({ scope, status, q: q || undefined }), staleTime: 15_000 });
  const today = todayIso();
  const weekEnd = plusDays(today, 7);
  const rows = useMemo(() => {
    const all = listQ.data ?? [];
    switch (due) {
      case 'overdue': return all.filter((t) => t.dueDate && t.dueDate < today && t.status !== '완료');
      case 'today': return all.filter((t) => t.dueDate === today);
      case 'week': return all.filter((t) => t.dueDate && t.dueDate >= today && t.dueDate <= weekEnd);
      case 'none': return all.filter((t) => !t.dueDate);
      default: return all;
    }
  }, [listQ.data, due, today, weekEnd]);
  const counts = useMemo(() => {
    const all = listQ.data ?? [];
    return {
      all: all.length,
      overdue: all.filter((t) => t.dueDate && t.dueDate < today && t.status !== '완료').length,
      today: all.filter((t) => t.dueDate === today).length,
      week: all.filter((t) => t.dueDate && t.dueDate >= today && t.dueDate <= weekEnd).length,
      none: all.filter((t) => !t.dueDate).length,
    };
  }, [listQ.data, today, weekEnd]);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['dashboard', 'today'] });
    void qc.invalidateQueries({ queryKey: ['nav', 'bell'] });
    void qc.invalidateQueries({ queryKey: ['salesActivity'] });
  };
  const patch = useMutation({ mutationFn: ({ id, p }: { id: number; p: Parameters<typeof patchTask>[1] }) => patchTask(id, p), onSuccess: invalidate, onError: (e: Error) => alert(`저장 실패: ${e.message}`) });
  const del = useMutation({ mutationFn: (id: number) => deleteTask(id), onSuccess: invalidate, onError: (e: Error) => alert(`지우기 실패: ${e.message}`) });

  // 한 줄 추가
  const [title, setTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [prio, setPrio] = useState<TaskPriority>('보통');
  const add = useMutation({ mutationFn: () => createTask({ title: title.trim(), dueDate: newDue || null, priority: prio }), onSuccess: () => { setTitle(''); setNewDue(''); setPrio('보통'); invalidate(); }, onError: (e: Error) => alert(`추가 실패: ${e.message}`) });

  // 제목 고치기
  const [editId, setEditId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const commitTitle = (t: Task): void => {
    const v = editTitle.trim();
    setEditId(null);
    if (v && v !== t.title) patch.mutate({ id: t.id, p: { title: v } });
  };

  return (
    <div className="p-8">
      <h1 className="ui-page-title">할 일</h1>
      <p className="ui-page-sub">영업건·거래처에 딸린 할 일과 그냥 할 일 — 기한이 오늘이거나 지난 것은 아침 8~10시 포털챗봇으로도 옵니다(마이페이지 › 알림에서 끔)</p>
      <LastUpdateBadge className="mb-3 mt-1" />

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <SegmentedControl ariaLabel="범위" value={scope} onChange={(k) => setParam('scope', k)} options={[{ key: 'mine', label: '내 것' }, { key: 'all', label: '전체' }]} />
        <SegmentedControl ariaLabel="상태" value={status} onChange={(k) => setParam('status', k)} options={[{ key: 'open', label: '열림' }, { key: 'done', label: '완료' }, { key: 'all', label: '전체' }]} />
        <SegmentedControl ariaLabel="기한" value={due} onChange={(k) => setParam('due', k === 'all' ? '' : k)} options={[
          { key: 'all', label: '기한 전체', count: counts.all },
          { key: 'overdue', label: '지남', count: counts.overdue, tone: counts.overdue ? 'warn' : 'default' },
          { key: 'today', label: '오늘', count: counts.today },
          { key: 'week', label: '이번 주', count: counts.week },
          { key: 'none', label: '기한 없음', count: counts.none },
        ]} />
        <input id="tasks-q" type="search" className="ui-input w-56" placeholder="제목 · 연결 대상 · 메모 검색" value={q} onChange={(e) => setParam('q', e.target.value)} />
      </div>

      <form className="flex flex-wrap items-center gap-2 mb-4 bg-white border border-slate-200 rounded p-3" onSubmit={(e) => { e.preventDefault(); if (title.trim()) add.mutate(); }}>
        <input id="tasks-new-title" className="ui-input flex-1 min-w-[240px]" placeholder="+ 새 할 일 — 예: 세종시청 규격서 회신 받기" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        <input id="tasks-new-due" type="date" className="ui-input" value={newDue} onChange={(e) => setNewDue(e.target.value)} title="기한" />
        <select id="tasks-new-prio" className="ui-input" value={prio} onChange={(e) => setPrio(e.target.value as TaskPriority)} title="우선순위">
          {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <button type="submit" className="btn btn-primary" disabled={!title.trim() || add.isPending}>추가</button>
        <span className="ui-help">담당은 나({user?.name ?? user?.userId}) — 영업건에 딸린 할 일은 영업건 상세 「활동」에서</span>
      </form>

      <div className="bg-white rounded border border-slate-200 overflow-x-auto">
        <table className="data-table w-full text-[13px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-8 text-center">✓</th>
              <th>제목</th>
              <th>연결</th>
              <th className="w-36">기한</th>
              <th className="w-24">우선순위</th>
              <th className="w-24">상태</th>
              <th className="w-24">담당</th>
              <th className="w-24">만든 이</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && <tr><td colSpan={9} className="px-2 py-6 text-center text-slate-400"><LoadingLines /></td></tr>}
            {listQ.isError && <tr><td colSpan={9} className="px-2 py-6 text-center text-red-600">불러오기 실패: {(listQ.error as Error).message}</td></tr>}
            {!listQ.isLoading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-8 text-center text-slate-400">해당하는 할 일이 없습니다 — 위 칸에 적고 [추가]</td></tr>
            )}
            {rows.map((t) => {
              const path = taskRelatedPath(t);
              const isDone = t.status === '완료';
              return (
                <tr key={t.id} className={`border-b border-slate-200 hover:bg-slate-50 ${isDone ? 'text-slate-400' : ''}`}>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" id={`task-${t.id}-done`} checked={isDone} disabled={patch.isPending} onChange={(e) => patch.mutate({ id: t.id, p: { status: e.target.checked ? '완료' : '미시작' } })} title={isDone ? '다시 열기' : '완료로 표시'} />
                  </td>
                  <td className="px-2 py-1">
                    {editId === t.id ? (
                      <input id={`task-${t.id}-title`} className="ui-input ui-input-sm w-full" value={editTitle} autoFocus onChange={(e) => setEditTitle(e.target.value)} onBlur={() => commitTitle(t)} onKeyDown={(e) => { if (e.key === 'Enter') commitTitle(t); if (e.key === 'Escape') setEditId(null); }} maxLength={200} />
                    ) : (
                      <button type="button" className={`text-left hover:underline ${isDone ? 'line-through' : ''}`} onClick={() => { setEditId(t.id); setEditTitle(t.title); }} title="누르면 제목을 고칩니다">
                        {t.priority === '높음' && !isDone && <span className="text-red-600 mr-1">❗</span>}{t.title}
                      </button>
                    )}
                    {t.note && <div className="text-xs text-slate-500 truncate max-w-[420px]" title={t.note}>{t.note}</div>}
                  </td>
                  <td className="px-2 py-1">
                    {t.relatedKind ? (
                      <span className="inline-flex items-center gap-1 min-w-0">
                        <span className="text-[11px] text-slate-500">{RELATED_LABEL[t.relatedKind] ?? t.relatedKind}</span>
                        {path ? <Link to={path} className="cell-link truncate max-w-[260px]" title={t.relatedLabel ?? ''}>{t.relatedLabel ?? t.relatedId}</Link> : <span className="truncate max-w-[260px]">{t.relatedLabel ?? t.relatedId}</span>}
                      </span>
                    ) : <span className="text-slate-300">-</span>}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <input id={`task-${t.id}-due`} type="date" className={`ui-input ui-input-sm w-[128px] ${t.overdue ? 'text-red-600' : ''}`} value={t.dueDate ?? ''} onChange={(e) => patch.mutate({ id: t.id, p: { dueDate: e.target.value || null } })} />
                      {t.dueDate && <span className={`text-[11px] whitespace-nowrap ${t.overdue ? 'text-red-600' : 'text-slate-500'}`}>{dueLabel(t.dueDate).slice(-3)}</span>}
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <select id={`task-${t.id}-prio`} className="ui-input ui-input-sm" value={t.priority} onChange={(e) => patch.mutate({ id: t.id, p: { priority: e.target.value as TaskPriority } })}>
                      {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select id={`task-${t.id}-status`} className="ui-input ui-input-sm" value={t.status} onChange={(e) => patch.mutate({ id: t.id, p: { status: e.target.value as TaskStatus } })}>
                      {TASK_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap">{t.assigneeName ?? t.assigneeUserId}</td>
                  <td className="px-2 py-1 whitespace-nowrap text-slate-500">{t.createdByName ?? t.createdBy}<div className="text-[11px] text-slate-400">{t.createdAt.slice(0, 10)}</div></td>
                  <td className="px-2 py-1 text-center">
                    <RowMenu items={[{ label: '지우기', tone: 'danger', onClick: () => { if (window.confirm(`「${t.title}」 을(를) 지울까요?`)) del.mutate(t.id); } }]} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-2">{rows.length}건{scope === 'mine' ? ' · 내 것(담당이 나 또는 내가 만든 것)' : ' · 전체'} · 완료로 표시한 것은 「완료」 필터에서 봅니다</p>
    </div>
  );
}
